import i18n from '@/utils/i18n';
import React, { useCallback, useEffect } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { ExternalContentFragment } from '@/types';
import { ExternalContentView } from './external-content-view';
import { StyleSheet, Text, View } from 'react-native';
import { useHolisticsURL } from '@/hooks/use-holistics-url';
import { useUniqueErrorToast } from '@/hooks/use-unique-toasts';
import { useUserContext } from '@/contexts/user-context';

interface Props {
  externalContent: ExternalContentFragment;
}

export const ExternalContentHolisticsDashboard: React.FC<Props> = ({
  externalContent,
}) => {
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const {
    selectedHolisticsLinkURL,
    holisticsLinkURLLoading,
    holisticsLinkURLError,
  } = useHolisticsURL(restaurantUUID, supplierUUID, externalContent);

  const showUniqueErrorToast = useUniqueErrorToast();

  useEffect(() => {
    if (holisticsLinkURLError) {
      showUniqueErrorToast(holisticsLinkURLError.message);
    }
  }, [holisticsLinkURLError, showUniqueErrorToast]);

  const renderContent = useCallback(() => {
    if (holisticsLinkURLLoading) {
      return (
        <View style={styles.contentWrapper}>
          <ActivityIndicator />
        </View>
      );
    } else if (!selectedHolisticsLinkURL) {
      return (
        <View style={styles.contentWrapper}>
          <Text style={styles.errorText}>
            {i18n.t('external_content.invalid_url')}
          </Text>
        </View>
      );
    } else {
      return (
        <ExternalContentView
          externalContentName={
            externalContent.display_name || externalContent.name
          }
          externalContentURL={selectedHolisticsLinkURL}
        />
      );
    }
  }, [externalContent, holisticsLinkURLLoading, selectedHolisticsLinkURL]);

  return <View style={styles.contentContainer}>{renderContent()}</View>;
};

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  contentWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 16,
  },
});
